import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StorageService } from 'src/app/core/services/StorageService/storage-service.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { URLBuilder } from 'src/app/core/utils/url-builder';

const AUTH_USER_KEY = 'auth_user';
const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  logoutEvent = new Subject<void>();
  private readonly USER_KEY = 'auth_user';
  private readonly TOKEN_KEY = 'authToken';
  private urlBuilder: URLBuilder;
  private authStatus$ = new BehaviorSubject<boolean>(false);
  private usersProfile: { [email: string]: {
    username: string;
    firstName: string;
    lastName: string;
    organization: string;
    location: string;
    email: string;
    phone: string;
    birthday: string;
    theme: string;
  }} = {
    'betha@betha.com.br': {
      username: 'bethaUser',
      firstName: 'Betha',
      lastName: 'Company',
      organization: 'Betha Systems',
      location: 'Brazil',
      email: 'betha@betha.com.br',
      phone: '123-456-7890',
      birthday: '1990-01-01',
      theme: 'betha-theme'
    },
    'fornecedor2@fornecedor2.com.br': {
      username: 'fornecedor2User',
      firstName: 'Fornecedor',
      lastName: 'Two',
      organization: 'Fornecedor Systems',
      location: 'Brazil',
      email: 'fornecedor2@fornecedor2.com.br',
      phone: '987-654-3210',
      birthday: '1985-05-15',
      theme: 'fornecedor-2-theme'
    }
  };

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private snackBar: MatSnackBar
  ) {
    this.urlBuilder = new URLBuilder();
  }

  ngOnInit() {
    // Atualizar o status de autenticação no ngOnInit
    this.authStatus$.next(this.isAuthenticated());
  }

  async getUserProfile(email: string) {
    return  this.usersProfile[email];
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime; // Returns true if the token is expired
    } catch (error) {
      console.error('Failed to parse token:', error);
      return true; // Consider the token expired if parsing fails
    }
  }

  notifyUser(message: string): void {
    this.snackBar.open(message, 'Fechar', { duration: 3000 });
  }

// Login local
loginLocal(email: string, password: string): Observable<boolean> {
  console.log('loginLocal called');
  const validUsers = this.getValidUsers(); 
  const user = validUsers.find(u => u.email === email && u.password === password);

  if (user) {
    this.storageService.setItem('auth_user', JSON.stringify({ email: user.email, theme: user.theme }));
    this.authStatus$.next(true); // Emit the authentication status update
    return of(true); // Return an Observable of `true` for successful login
  }

  return of(false); // Return an Observable of `false` for failed login
}

  isAuthenticated(): boolean {
    return !!this.storageService.getItem(AUTH_USER_KEY);
  }

  logout(): void {
    this.storageService.removeItem(AUTH_USER_KEY); // Remove dados do usuário
    this.storageService.clearTokens(); // Limpa tokens
    this.storageService.removeTheme(); // Remove tema
    this.authStatus$.next(false);
    this.logoutEvent.next();
  }

  get authStatus(): Observable<boolean> {
    return this.authStatus$.asObservable();
  }

  async getUser(): Promise<{ email: string; theme: string } | null> {
    return await this.storageService.getObjectAsync<{
      email: string;
      theme: string;
    }>(AUTH_USER_KEY);
  }

  async storeUser(user: { email: string; theme: string }): Promise<void> {
    await this.storageService.setObjectAsync(AUTH_USER_KEY, user);
  }

  authenticate(loginData: { login: string; senha: string }): Observable<string | null> {
    const url = this.urlBuilder.buildEndpoint(environment.security.login);
    console.log('Authenticating user with URL:', url); 

    return this.http
      .post<{ listaObj: { token: string }[] }>(url, loginData)
      .pipe(
        map((response) => {
          const token = response.listaObj?.[0]?.token || null;
          if (token) {
            this.setToken(token); // Armazena o token no localStorage
          }
          return token;
        }),
        catchError((err) => {
          console.error('Erro durante a autenticação:', err);
          this.notifyUser('Erro durante a autenticação. Verifique suas credenciais.');
          return of(null);
        })
      );
  }

  checkToken(): Observable<boolean> {
    const token = this.getToken();
    return token ? this.verifyToken(token) : of(false);
  }

  verifyToken(token: string): Observable<boolean> {
    const url = this.urlBuilder.buildEndpoint(environment.security.checkToken);

    return this.http
      .get<{ valid: boolean }>(url, { headers: this.getAuthHeaders() })
      .pipe(
        map((response) => response.valid || false),
        catchError(() => of(false))
      );
  }

  async refreshTokenIfNeeded(): Promise<void> {
    let token = await this.getTokenAsync();

    if (!token || this.isTokenExpired(token)) {
      const refreshToken = await this.getRefreshToken();
      if (refreshToken) {
        token = await firstValueFrom(this.refreshToken(refreshToken));
        if (token) {
          await this.setToken(token);
        }
      } else {
        // Caso não haja refresh token, reautenticar o usuário
        token = await firstValueFrom(
          this.authenticate({ login: 'user@example.com', senha: 'password' })
        );
        if (token) {
          await this.setToken(token);
        }
      }
    }
  }

  async setToken(token: string): Promise<void> {
    await this.storageService.setItemAsync(AUTH_TOKEN_KEY, token);
  }

getToken(): string | null {
  const token = this.storageService.getItem(AUTH_TOKEN_KEY);
  return token || null;
}

  async getTokenAsync(): Promise<string | null> {
    let token = await this.storageService.getItemAsync(AUTH_TOKEN_KEY);
  
    // Verifica se o token está ausente ou expirado
    if (!token || this.isTokenExpired(token)) {
      // Tenta usar o refresh token para obter um novo token
      const refreshToken = await this.storageService.getItemAsync(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        token = await firstValueFrom(this.refreshToken(refreshToken));
      } else {
        // Se não houver refresh token, autentica novamente
        token = await firstValueFrom(this.authenticate({ login: '33870041000198', senha: 'HgtUop@8' }));
      }
  
      // Salva o novo token, se obtido
      if (token) {
        await this.storageService.setItemAsync(AUTH_TOKEN_KEY, token);
      }
    }
  
    return token;
  }
  

  async clearTokens(): Promise<void> {
    await this.storageService.clearTokensAsync();
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.storageService.getItemAsync(REFRESH_TOKEN_KEY);
  }

  refreshToken(refreshToken: string): Observable<string | null> {
    const url = this.urlBuilder.buildEndpoint(
      environment.security.refreshToken
    );

    return this.http.post<{ token: string }>(url, { refreshToken }).pipe(
      timeout(5000), // 5-second timeout
      map((response) => response?.token || null),
      catchError((error) => {
        console.error('Failed to refresh token:', error);
        return of(null);
      })
    );
  }

  private getValidUsers(): {
    email: string;
    password: string;
    theme: string;
  }[] {
    return [
      {
        email: 'betha@betha.com.br',
        password: 'senha123',
        theme: 'betha-theme',
      },
      {
        email: 'fornecedor2@fornecedor2.com.br',
        password: 'senha321',
        theme: 'fornecedor-2-theme',
      },
    ];
  }

  private getAuthHeaders(): { Authorization: string } {
    const token = this.getToken();
    return { Authorization: `Bearer ${token}` };
  }
}
